import { useEffect, useState } from "react";
import AddPayoutModal from "./modal/AddPayoutModal";
import { getRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import { toast } from "react-toastify";
import PaginationComponent from "../../Component/Pagination/pagination";
import moment from "moment";
import Loader from "../../Component/Loader/Loader";

// Interfaces
interface StatItem {
    value: number;
    growth: number;
}

interface PayoutStats {
    totalPayout: StatItem;
    avgPayout: StatItem;
    pendingPayout: StatItem;
    failedPayout: StatItem;
    activeAffiliates: StatItem;
}

// Fixed Status List & Colors
const orderStatusList = ["CAPTURE", "INPROCESS", "COMPLETED", "FAILED"];
const STATUS_COLORS = {
    0: '#0088FE', // CAPTURE
    1: '#FFBB28', // INPROCESS
    2: '#00C49F', // COMPLETED
    3: '#FF8042'  // FAILED
};

const AffiliatePayout = () => {
    const [showModal, setShowModal] = useState(false);
    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    const [affiliatePayoutList, setAffiliatePayoutList] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalUsers, setTotalUsers] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);

    // Search and Filters
    const [searchText, setSearchText] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    // Date Filters
    const [dateFilter, setDateFilter] = useState("thisMonth");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Stats & Chart Data
    const [stats, setStats] = useState<PayoutStats>({
        totalPayout: { value: 0, growth: 0 },
        avgPayout: { value: 0, growth: 0 },
        pendingPayout: { value: 0, growth: 0 },
        failedPayout: { value: 0, growth: 0 },
        activeAffiliates: { value: 0, growth: 0 }
    });
    const [dailyData, setDailyData] = useState<any[]>([]);
    const [pieData, setPieData] = useState<any[]>([]);

    // Handle Date Calculation
    useEffect(() => {
        let start = "";
        let end = "";

        if (dateFilter === "7") {
            start = moment().subtract(7, 'days').format('YYYY-MM-DD');
            end = moment().format('YYYY-MM-DD');
        } else if (dateFilter === "30") {
            start = moment().subtract(30, 'days').format('YYYY-MM-DD');
            end = moment().format('YYYY-MM-DD');
        } else if (dateFilter === "thisMonth") {
            start = moment().startOf('month').format('YYYY-MM-DD');
            end = moment().endOf('month').format('YYYY-MM-DD');
        } else if (dateFilter === "lastMonth") {
            start = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
            end = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        } else if (dateFilter === "allTime") {
            start = "";
            end = "";
        }

        setStartDate(start);
        setEndDate(end);
        setPage(1);
    }, [dateFilter]);

    const fetchAffiliateList = async () => {
        if (dateFilter !== "allTime" && (!startDate || !endDate)) return;

        setLoading(true);
        try {
            const response = await getRequest(
                `/affiliate/affiliate-payout-list?page=${page}&limit=10&search=${searchQuery}&status=${statusFilter}&startDate=${startDate}&endDate=${endDate}`
            );

            if (response.status === "success") {
                setAffiliatePayoutList(response.data);
                setTotalPages(response.pagination.totalPages);
                setTotalUsers(response.pagination.total);
                
                if (response.stats) setStats(response.stats);
                if (response.charts) {
                    setDailyData(response.charts.daily || []);
                    setPieData(response.charts.pie || []);
                }
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error("Error fetching affiliates:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAffiliateList();
    }, [page, searchQuery, statusFilter, startDate, endDate]);

    // Debounce search
    useEffect(() => {
        const delay = setTimeout(() => {
            setSearchQuery(searchText);
            setPage(1);
        }, 500);
        return () => clearTimeout(delay);
    }, [searchText]);

    const changeSettlementStatus = async (id: any, status: any) => {
        try {
            if(status === 0){
                toast.error("You cannot change status to CAPTURE");
                return;
            }
            if(status === 1){
                toast.error("You cannot change status to INPROCESS");
                return;
            }
            setLoading(true);

            const response = await getRequest(`/affiliate/changeSettlementStatus/${id}/${status}`);

            if (response.status === "success") {
                toast.success(response.message);
                fetchAffiliateList();
            } else if (response.status === "session_expired") {
                localStorage.removeItem("UOMOX_AFF_TOKEN");
                window.location.href = "/";
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error("Error updating settlement status:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    // --- Dynamic Reusable Components ---
    const GrowthBadge = ({ growth }: { growth: number }) => {
        const isPositive = growth >= 0;
        return (
            <div className="d-flex align-items-center gap-2 mt-1">
                <span className={`badge badge-sr ${isPositive ? 'bg-success-light-5 text-success' : 'bg-danger-light-5 text-danger'} mb-0`}>
                    <i className={`ri-arrow-${isPositive ? 'up' : 'down'}-line align-middle`}></i> {Math.abs(growth).toFixed(2)} % 
                </span>
                <span className="fs-13 text-muted">vs. previous month</span>
            </div>
        );
    };

    const maxDailyValue = Math.max(...dailyData.map(d => d.total), 1);

    const generatePieGradient = () => {
        if (pieData.length === 0) return '#e9ecef';
        const total = pieData.reduce((acc, curr) => acc + curr.value, 0);
        let cumulativePct = 0;
        
        const stops = pieData.map((d) => {
            const pct = (d.value / total) * 100;
            const startStr = `${cumulativePct}%`;
            cumulativePct += pct;
            const endStr = `${cumulativePct}%`;
            return `${STATUS_COLORS[d.status as keyof typeof STATUS_COLORS] || '#6c757d'} ${startStr} ${endStr}`;
        });
        
        return `conic-gradient(${stops.join(', ')})`;
    };

    return (
        <>
            <div className="hk-pg-wrapper">
                {loading && (<Loader />)}
                <div className="hk-pg-body py-0">
                    <div className="page-wrap">
                        <div className="page-content ps-0">
                            <div className="page-detail-wrap">
                                <header className="page-header">
                                    <div>
                                        <div className="page-title">
                                            <h2 className="mb-0">Settlements Payouts</h2>
                                        </div>
                                        <p className="text-muted mb-0">All commission transactions across affiliates</p>
                                    </div>
                                    <div className="page-options-wrap">
                                        <button type="button" className="btn btn-dark btn-rounded" onClick={(e) => handleOpenModal()}>
                                            <span>
                                                <span className="icon"><i className="ri-add-line bticon"></i></span>
                                                <span>Add payout</span>
                                            </span>
                                        </button>
                                        <a className="btn btn-icon btn-flush-dark btn-rounded flush-soft-hover hk-navbar-togglable d-lg-inline-block d-none"
                                            href="#" data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse">
                                            <span className="btn-icon-wrap">
                                                <span className="feather-icon"><i className="ri-arrow-up-s-line ri-lg"></i></span>
                                                <span className="feather-icon d-none"><i className="ri-arrow-down-s-line ri-lg"></i></span>
                                            </span>
                                        </a>
                                    </div>
                                </header>
                                <div className="page-body">
                                    <div data-simplebar className="nicescroll-bar">
                                        <div className="container-fluid">
                                            
                                            {/* TOP CARDS */}
                                            <div className="row g-3">
                                                <div className="col">
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between">
                                                                <div>
                                                                    <h6 className="h2 mb-1">₹{stats.totalPayout.value.toFixed(2)}</h6>
                                                                    <p className="mb-1 text-muted fs-12">Total Paid Out</p>
                                                                </div>
                                                                <div className="flex-shrink-0">
                                                                    <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                        <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <GrowthBadge growth={stats.totalPayout.growth} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col">
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between">
                                                                <div>
                                                                    <h6 className="h2 mb-1">₹{stats.avgPayout.value.toFixed(2)}</h6>
                                                                    <p className="mb-1 text-muted fs-12">Avg Payout</p>
                                                                </div>
                                                                <div className="flex-shrink-0">
                                                                    <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                        <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <GrowthBadge growth={stats.avgPayout.growth} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col">
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between">
                                                                <div>
                                                                    <h6 className="h2 mb-1">₹{stats.pendingPayout.value.toFixed(2)}</h6>
                                                                    <p className="mb-1 text-muted fs-12">Pending Payouts</p>
                                                                </div>
                                                                <div className="flex-shrink-0">
                                                                    <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                        <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <GrowthBadge growth={stats.pendingPayout.growth} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col">
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between">
                                                                <div>
                                                                    <h6 className="h2 mb-1">₹{stats.failedPayout.value.toFixed(2)}</h6>
                                                                    <p className="mb-1 text-muted fs-12">Failed payouts</p>
                                                                </div>
                                                                <div className="flex-shrink-0">
                                                                    <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                        <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <GrowthBadge growth={stats.failedPayout.growth} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col">
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between">
                                                                <div>
                                                                    <h6 className="h2 mb-1">{stats.activeAffiliates.value}</h6>
                                                                    <p className="mb-1 text-muted fs-12">Active Affiliates</p>
                                                                </div>
                                                                <div className="flex-shrink-0">
                                                                    <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                        <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2 mt-1">
                                                                <span className="badge badge-sr bg-success-light-5 text-success mb-0"><i className="ri-arrow-up-line align-middle"></i>0 % </span>
                                                                <span className="fs-13 text-muted">vs. previous month</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* CHARTS SECTION */}
                                            <div className="row g-3 mb-3">
                                                {/* Daily Chart */}
                                                <div className="col-lg-4">
                                                    <div className="card h-100">
                                                        <div className="card-header">
                                                            <h6>Monthly Payouts (Day Wise)</h6>
                                                        </div>
                                                        <div className="card-body p-3">
                                                            <div className="d-flex align-items-end justify-content-between h-100 w-100 pt-4 pb-2 border-bottom border-light gap-1" style={{ minHeight: "220px" }}>
                                                                {dailyData.length > 0 ? dailyData.map((d, idx) => {
                                                                    const barHeightPct = Math.max((d.total / maxDailyValue) * 100, 2);
                                                                    return (
                                                                        <div 
                                                                            key={idx} 
                                                                            className="flex-grow-1 position-relative" 
                                                                            style={{ height: `${barHeightPct}%`, backgroundColor: '#00C49F', borderRadius: '3px 3px 0 0', cursor: 'pointer', transition: 'height 0.3s ease' }}
                                                                            title={`${moment(d.date).format('MMM DD')}: ₹${d.total.toFixed(2)}`}
                                                                        ></div>
                                                                    )
                                                                }) : (
                                                                    <div className="w-100 d-flex justify-content-center align-items-center text-muted h-100">No data for selected period</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Pie Chart */}
                                                <div className="col-lg-4">
                                                    <div className="card h-100">
                                                        <div className="card-header">
                                                            <h6>Payout Status Breakdown</h6>
                                                        </div>
                                                        <div className="card-body d-flex align-items-center justify-content-center flex-column flex-sm-row gap-4 p-4">
                                                            {pieData.length > 0 ? (
                                                                <>
                                                                    <div style={{ width: "160px", height: "160px", borderRadius: "50%", background: generatePieGradient(), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)" }}>
                                                                        <div style={{ width: "90px", height: "90px", backgroundColor: "#fff", borderRadius: "50%" }}></div>
                                                                    </div>
                                                                    <div className="d-flex flex-column gap-2" style={{ maxHeight: "160px", overflowY: "auto" }}>
                                                                        {pieData.map((d, idx) => (
                                                                            <div key={idx} className="d-flex align-items-center gap-2 fs-13">
                                                                                <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: STATUS_COLORS[d.status as keyof typeof STATUS_COLORS] }}></span>
                                                                                <span className="text-dark fw-medium text-truncate" style={{ maxWidth: '120px' }}>{orderStatusList[d.status]}</span>
                                                                                <span className="text-muted ms-auto">₹{d.value.toFixed(2)}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="text-muted">No payouts found.</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                 <div className="col-lg-4">
                                                    <div className="card h-100">
                                                        <div className="card-header">
                                                            <h6>Top Affiliates by Earnings</h6>
                                                        </div>
                                                        <div className="card-body"></div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* DATA TABLE */}
                                            <div className="row">
                                                <div className="col-12">
                                                    <div className="card">
                                                        <div className="card-header">
                                                            <div>
                                                                <h6 className="mb-0">Payout Transactions</h6>
                                                                <p className="mb-0 text-muted fs-12">All commission transactions across affiliates</p>
                                                            </div>
                                                            <div>
                                                                <div className="filters d-flex gap-2">
                                                                    {/* Custom Date Filters */}
                                                                    <select className="form-select form-select-sm w-130p" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                                                                        <option value="7">Last 7 Days</option>
                                                                        <option value="30">Last 30 Days</option>
                                                                        <option value="thisMonth">This Month</option>
                                                                        <option value="lastMonth">Last Month</option>
                                                                        <option value="allTime">All Time</option>
                                                                    </select>
                                                                    <div className="search-wrap">
                                                                        <svg className="s-icon" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#b0a898" strokeWidth="1.5"><circle cx="6.5" cy="6.5" r="4"></circle><line x1="10" y1="10" x2="14" y2="14"></line></svg>
                                                                        <input type="text" placeholder="Search affiliate, order ID…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                                                                    </div>
                                                                    <select className="fsel" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                                                                        <option value="">All status</option>
                                                                        {orderStatusList.map((status, index) => (
                                                                            <option key={index} value={index}>{status}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="table-responsive">
                                                            <table className="table table-hover mb-0">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Order ID</th>
                                                                        <th>Affiliate</th>
                                                                        <th className="text-center">Amount</th>
                                                                        <th className="text-center">Status</th>
                                                                        <th>Date</th>
                                                                        <th className="text-center">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {affiliatePayoutList && affiliatePayoutList.length > 0 ? (
                                                                        affiliatePayoutList.map((item, index) => (
                                                                            <tr key={index}>
                                                                                <td>#{item.ap_unique_id}</td>
                                                                                <td>
                                                                                    <div className="media align-items-center">
                                                                                        <div className="media-head me-2">
                                                                                            <div className="avatar avatar-xss rounded">
                                                                                                <img src="/dist/img/logo-avatar-3.png" alt="user" className="avatar-img" />
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="media-body">
                                                                                            <h6 className="fs-13 mb-0 eclps-name-175">{item.affiliate_first_name + ' ' + item.affiliate_last_name}</h6>
                                                                                            <p className="mb-0 fs-12">{item.affiliate_email}</p>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="text-center">₹{item.ap_amount}</td>
                                                                                <td className="text-center">
                                                                                    <div className="btn-group material-shadow" role="group" style={{ position: 'static' }}>
                                                                                        <div className="btn-group" role="group" style={{ position: 'static' }}>
                                                                                            <button id={`btnGroupDrop${item._id}`} type="button" className="btn btn-sm btn-primary dropdown-toggle material-shadow-none drop-text" data-bs-toggle="dropdown" aria-expanded="false">
                                                                                                {orderStatusList[item.ap_status] || "Processing"}
                                                                                            </button>
                                                                                            <ul className="dropdown-menu" aria-labelledby={`btnGroupDrop${item._id}`}>
                                                                                                {orderStatusList.map((status: any, index: any) => (
                                                                                                    <li key={index}>
                                                                                                        <button
                                                                                                            className={`dropdown-item ${item.ap_status === index ? "active" : ""}`}
                                                                                                            onClick={() => changeSettlementStatus(item._id, index)}
                                                                                                        >
                                                                                                            {status}
                                                                                                        </button>
                                                                                                    </li>
                                                                                                ))}
                                                                                            </ul>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td>{moment(item.createdAt).format('DD MMM, YYYY')}</td>
                                                                                <td className="text-center">
                                                                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                                                                        <div className="d-flex gap-1">
                                                                                            <a className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover" data-bs-toggle="tooltip" data-placement="top" title="View" href="#" >
                                                                                                <span className="icon">
                                                                                                    <i className="ri-eye-line"></i>
                                                                                                </span>
                                                                                            </a>
                                                                                            <div className="dropdown dropdown-static">
                                                                                                <button className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover dropdown-toggle no-caret" aria-expanded="false" data-bs-toggle="dropdown">
                                                                                                    <span className="icon">
                                                                                                        <i className="ri-more-2-fill"></i>
                                                                                                    </span>
                                                                                                </button>
                                                                                                <div className="dropdown-menu dropdown-menu-end" data-popper-placement="bottom-end">
                                                                                                    <a className="dropdown-item" href="#">
                                                                                                        <span className="dropdown-icon"><i className="ri-vip-crown-line"></i></span>
                                                                                                        <span>Edit</span>
                                                                                                    </a>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    ) : (
                                                                        <tr>
                                                                            <td colSpan={6}>
                                                                                <div className="text-center">No Record Found</div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="card-footer d-flex justify-content-between align-items-center">
                                                            <div className="col-sm">
                                                                <div className="text-muted">Showing <span className="fw-semibold">{affiliatePayoutList?.length || 0}</span> of <span className="fw-semibold">{totalUsers || 0}</span> Results</div>
                                                            </div>
                                                            {totalPages > 0 && <PaginationComponent totalPages={totalPages} currentPage={page} setCurrentPage={setPage} />}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AddPayoutModal
                show={showModal}
                onHide={handleCloseModal}
            />
        </>
    )
}
export default AffiliatePayout;